import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import {
  Collapse,
  Container,
  Nav,
  NavLink,
} from 'reactstrap';

import { APIgetPage } from '../../../api/Pages';

import { slugify } from '../../../helpers';
import {
  getCollectionDestination,
  getFileDestination,
  normalizePageVisibilityMode,
} from '../../../helpers/PageSections';
import { isValidUrl } from '../../../helpers/Validators';
import {
  usePageBasePath,
  usePageId,
  usePageIsEditing,
  usePageRouteId,
  usePageVisibilityMode,
} from '../../../helpers/PageIdContext';
import { normalizeHydratedPageFile } from './Common/pageFileHydration';
import { getPageFileByUuid } from './Common/pageFiles';
import RadioField from './Common/RadioField';

import PageNavigationItemsField, {
  normalizePageNavigationItem,
} from './Common/PageNavigationItemsField';
import formatIntl from './Common/formatIntl';
import { withLayout } from './PuckLayout';

const ORIENTATIONS = ['horizontal', 'vertical'];
const ALIGNS = ['left', 'center', 'right'];
const WIDTHS = ['container', 'full'];
const pageSlugByIdCache = new Map();

const toPagePath = (pageRouteToken, pageBasePath = 'pages') => {
  if (pageRouteToken == null || pageRouteToken === '') return '';

  const routeToken = String(pageRouteToken);
  if (isValidUrl(routeToken)) return routeToken;

  if (routeToken.startsWith('/')) {
    return routeToken;
  }

  return `/${pageBasePath}/${routeToken}`;
};

const getAlignStyle = (orientation, align) => {
  if (orientation === 'vertical') {
    let alignItems = 'flex-start';
    if (align === 'center') {
      alignItems = 'center';
    } else if (align === 'right') {
      alignItems = 'flex-end';
    }

    return {
      display: 'flex',
      flexDirection: 'column',
      alignItems,
      width: '100%',
    };
  }

  let justifyContent = 'flex-start';
  if (align === 'center') {
    justifyContent = 'center';
  } else if (align === 'right') {
    justifyContent = 'flex-end';
  }

  return {
    display: 'flex',
    flexDirection: 'row',
    justifyContent,
    width: '100%',
  };
};
const resolveNavigationDestination = ({
  item,
  pageVisibilityMode,
  pageBasePath,
  pageRouteId,
  pageId,
  backUrl,
}) => {
  if (!item) {
    return '';
  }

  if (item.targetType === 'collection' && item.targetCollection?.uuid) {
    return getCollectionDestination({
      collection: item.targetCollection,
      pageVisibilityMode,
      pageBasePath,
      pageRouteId,
    });
  }

  if (item.targetType === 'file' && item.targetFile?.uuid) {
    return getFileDestination({
      file: item.targetFile,
      pageVisibilityMode,
      pageBasePath,
      pageRouteId,
      pageId,
      backUrl,
    });
  }

  if (item.targetType === 'page' && (item.targetPage?.id || item.targetPage?.url)) {
    const pageRouteToken = item.targetPage?.slugResolved
      ? (item.targetPage?.url || item.targetPage?.id)
      : (item.targetPage?.id || item.targetPage?.url);

    return toPagePath(pageRouteToken, pageBasePath);
  }

  if (item.targetType === 'url' && item.url) {
    return item.url;
  }

  return '';
};

const PuckNavigationRender = ({
  items,
  orientation,
  align,
  width,
  page_visibility_mode: pageVisibilityMode,
}) => {
  const location = useLocation();
  const pageId = usePageId();
  const isEditing = usePageIsEditing();
  const pageRouteId = usePageRouteId();
  const pageBasePath = usePageBasePath();
  const contextVisibilityMode = usePageVisibilityMode();
  const [resolvedItems, setResolvedItems] = useState(Array.isArray(items) ? items : []);

  const normalizedOrientation = ORIENTATIONS.includes(orientation) ? orientation : 'horizontal';
  const normalizedAlign = ALIGNS.includes(align) ? align : 'left';
  const normalizedWidth = WIDTHS.includes(width) ? width : 'container';
  const effectivePageVisibilityMode = normalizePageVisibilityMode(
    pageVisibilityMode || contextVisibilityMode,
  );
  const backUrl = `${location.pathname}${location.search || ''}`;

  useEffect(() => {
    let mounted = true;
    const sourceItems = Array.isArray(items) ? items.map(normalizePageNavigationItem) : [];

    const pageItems = sourceItems.filter(
      item => item.targetType === 'page' && item.targetPage?.id,
    );
    const fileItems = sourceItems.filter(
      item => item.targetType === 'file' && item.targetFile?.uuid,
    );

    if (pageItems.length === 0 && (fileItems.length === 0 || !pageId)) {
      setResolvedItems(sourceItems);
      return () => {
        mounted = false;
      };
    }

    const pagePromise = Promise.all(pageItems.map((item) => {
      const pageTargetId = item.targetPage?.id;
      if (!pageTargetId) {
        return Promise.resolve(null);
      }

      const cacheKey = String(pageTargetId);
      const cachedSlug = pageSlugByIdCache.get(cacheKey);

      return APIgetPage(pageTargetId, isEditing ? { edit_mode: true } : {})
        .then((response) => {
          const currentSlug = response?.data?.url || cachedSlug || item.targetPage?.url || '';
          if (currentSlug) {
            pageSlugByIdCache.set(cacheKey, currentSlug);
          }

          return {
            id: pageTargetId,
            slug: currentSlug,
          };
        })
        .catch(() => ({
          id: pageTargetId,
          slug: cachedSlug || item.targetPage?.url || '',
        }));
    }));

    const filePromise = (!pageId || fileItems.length === 0)
      ? Promise.resolve([])
      : Promise.all(fileItems.map((item) => getPageFileByUuid({
        pageId,
        fileUuid: item.targetFile.uuid,
        fallbackFile: item.targetFile,
      })
        .then((resolved) => ({
          uuid: item.targetFile.uuid,
          file: normalizeHydratedPageFile(item.targetFile, resolved || item.targetFile),
        }))
        .catch(() => ({
          uuid: item.targetFile.uuid,
          file: item.targetFile,
        }))));

    Promise.all([pagePromise, filePromise])
      .then(([resolvedPages, resolvedFiles]) => {
        if (!mounted) {
          return;
        }

        const slugByPageId = {};
        resolvedPages.forEach((entry) => {
          if (entry?.id) {
            slugByPageId[String(entry.id)] = entry.slug || '';
          }
        });

        const fileByUuid = {};
        resolvedFiles.forEach((entry) => {
          if (entry?.uuid) {
            fileByUuid[String(entry.uuid)] = entry.file;
          }
        });

        setResolvedItems(sourceItems.map((item) => {
          if (item.targetType === 'file' && item.targetFile?.uuid) {
            const hydratedFile = fileByUuid[String(item.targetFile.uuid)] || item.targetFile;
            return {
              ...item,
              targetFile: hydratedFile,
            };
          }

          if (item.targetType !== 'page' || !item.targetPage?.id) {
            return item;
          }

          const resolvedSlug = slugByPageId[String(item.targetPage.id)] || item.targetPage.url || '';

          return {
            ...item,
            targetPage: {
              ...item.targetPage,
              url: resolvedSlug,
              slugResolved: true,
            },
          };
        }));
      });

    return () => {
      mounted = false;
    };
  }, [items, pageId, isEditing]);

  const navigation = useMemo(() => resolvedItems.map((item) => {
    const normalizedItem = normalizePageNavigationItem(item);
    const destination = resolveNavigationDestination({
      item: normalizedItem,
      pageVisibilityMode: effectivePageVisibilityMode,
      pageBasePath,
      pageRouteId,
      pageId,
      backUrl,
    });

    return {
      ...normalizedItem,
      url: destination,
      class: normalizedItem.className,
    };
  }), [
    backUrl,
    effectivePageVisibilityMode,
    pageBasePath,
    pageId,
    pageRouteId,
    resolvedItems,
  ]);

  const navContent = (
    <Collapse isOpen={true} navbar className="nav-actions nav-actions-desktop">
      <Nav className="nav-main" style={getAlignStyle(normalizedOrientation, normalizedAlign)}>
        {navigation && navigation.length > 0 && (
          <>
            {navigation.map((nitem, idx) => {
              const key = `${slugify(nitem.title)}-${idx + 1}`;
              const destination = nitem.url;
              const shouldUseAnchor = nitem.targetType === 'url';

              return (
                <li key={key} className={nitem.class}>
                  {shouldUseAnchor ? (
                    <a
                      href={destination}
                      target={isValidUrl(destination) ? '_blank' : undefined}
                      rel={isValidUrl(destination) ? 'noopener noreferrer' : undefined}
                      className="nav-link"
                    >
                      {nitem.title}
                    </a>
                  ) : (
                    <NavLink to={destination || '#'} tag={RouterNavLink}>
                      {nitem.title}
                    </NavLink>
                  )}
                </li>
              );
            })}
          </>
        )}
      </Nav>
    </Collapse>
  );

  if (normalizedWidth === 'full') {
    return (
      <div className="custom-page-section puck-navigation puck-navigation-full">
        {navContent}
      </div>
    );
  }

  return (
    <div className="custom-page-section puck-navigation puck-navigation-container">
      <Container>
        {navContent}
      </Container>
    </div>
  );
};

const NavigationRadioField = ({
  id,
  name,
  value,
  onChange,
  field,
  readOnly,
}) => (
  <RadioField
    id={id}
    name={name}
    label={field.label}
    value={value}
    onChange={onChange}
    options={field.options || []}
    readOnly={readOnly}
    fitContent
  />
);

NavigationRadioField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  field: PropTypes.shape({
    label: PropTypes.string,
    options: PropTypes.array,
  }).isRequired,
};

NavigationRadioField.defaultProps = {
  id: '',
  name: '',
  value: null,
  readOnly: false,
};

PuckNavigationRender.propTypes = {
  items: PropTypes.array,
  orientation: PropTypes.oneOf(ORIENTATIONS),
  align: PropTypes.oneOf(ALIGNS),
  width: PropTypes.oneOf(WIDTHS),
  page_visibility_mode: PropTypes.string,
};

PuckNavigationRender.defaultProps = {
  items: [],
  orientation: 'horizontal',
  align: 'left',
  width: 'container',
  page_visibility_mode: null,
};

const PuckNavigation = {
  get label() { return formatIntl('Navigation'); },

  fields: {
    items: {
      type: 'custom',
      get label() { return formatIntl('Items'); },
      render: (props) => <PageNavigationItemsField {...props} />,
    },
    orientation: {
      type: 'radio',
      get label() { return formatIntl('View'); },
      options: [
        { get label() { return formatIntl('Horizontal'); }, value: 'horizontal' },
        { get label() { return formatIntl('Vertical'); }, value: 'vertical' },
      ],
      render: (props) => <NavigationRadioField {...props} />,
    },
    align: {
      type: 'radio',
      get label() { return formatIntl('Align'); },
      options: [
        { get label() { return formatIntl('Left'); }, value: 'left' },
        { get label() { return formatIntl('Center'); }, value: 'center' },
        { get label() { return formatIntl('Right'); }, value: 'right' },
      ],
      render: (props) => <NavigationRadioField {...props} />,
    },
    width: {
      type: 'radio',
      get label() { return formatIntl('Width'); },
      options: [
        { get label() { return formatIntl('Container'); }, value: 'container' },
        { get label() { return formatIntl('Full width'); }, value: 'full' },
      ],
      render: (props) => <NavigationRadioField {...props} />,
    },
  },

  get defaultProps() {
    return {
      items: [
        normalizePageNavigationItem({
          title: formatIntl('Nav item'),
          targetType: 'url',
          url: '',
        }, 0),
      ],
      orientation: 'horizontal',
      align: 'left',
      width: 'container',
      layout: {
        padding: '0px',
      },
    };
  },

  render: (props) => <PuckNavigationRender {...props} />,
};

export default withLayout(PuckNavigation);
